// pages/terms-conditions.tsx
import Head from 'next/head';
import Link from 'next/link';
import { Container } from 'react-bootstrap';
import { ArrowRight } from '../assets/icons';

const LAST_UPDATED = 'August 2025';

/**
 * Content source:  &quot;Website and Patient Platform Terms of Use &quot; (uploaded docx).
 * Paste the rest of the sections from your doc below following the same shape.
 */

function TermsConditions() {
  return (
    <div className='tw-bg-white'>
      <Head>
        <title>Terms & Conditions - 24hrdoc</title>
        <meta
          name='description'
          content='Review 24hrdoc’s Terms of Use for website and platform rules, including user responsibilities, service limits, dispute resolution, and policies.'
        />
        <link rel='canonical' href='https://www.24hrdoc.com/terms-conditions' />
        <meta
          property='og:image'
          content='https://www.24hrdoc.com/_next/image?url=%2Fassets%2Ficons%2FLogoTransparent.png&w=256&q=75'
        />
      </Head>

      <Container>
        {/* Breadcrumb */}
        <nav className='tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-my-6'>
          <Link href='/' className='tw-text-gray-500 hover: '>
            Home
          </Link>
          <ArrowRight width={16} height={16} stroke='#53575A' />
          <Link href='/terms-conditions' className=' '>
            Terms & Conditions
          </Link>
        </nav>

        {/* Header */}
        <header className='tw-mb-8'>
          <h1 className='tw-text-3xl tw-font-semibold tw-text-gray-900'>
            Terms & <span className=' gradientText'>Conditions</span>
          </h1>
          <p className='tw-text-sm tw-text-gray-500 tw-mt-2'>
            Last Updated: {LAST_UPDATED}
          </p>
        </header>

        <div className='tw-space-y-6'>
          <p className='  tw-font-semibold'>24HrDoc, INc. Terms of Use</p>
          <p className=' '>
            <span className=' tw-font-semibold'>
              PLEASE CAREFULLY READ THE LINKED TERMS OF USE (the “Terms”) BEFORE
              USING THE 24Hrdoc, inc. website (the “Website”) and THE 24hrdoc
              patient PLATFORM (collectively, the “Platform”).
            </span>{' '}
            The Website and Platform, including all relevant content, products,
            services, and functionality associated with the Website and Platform
            and any other affiliated software or application owned by 24HrDoc,
            Inc., are collectively referred to as the “Services.”
          </p>
          <p className='  tw-font-semibold'>
            BY ACCESSING OR USING THE SERVICES, YOU CONSENT TO BE BOUND BY THESE
            TERMS. IF YOU DO NOT AGREE TO ACCEPT THESE TERMS, DO NOT ACCESS OR
            USE THE SERVICES.
          </p>
          <p className=' '>
            Any information that 24HrDoc, Inc. (“
            <span className='tw-font-semibold'>24HrDoc</span>” or “
            <span className='tw-font-semibold'>we</span>”) collects through your
            use of the Services is subject to the 24HrDoc Privacy Policy, which
            is part of these Terms.
          </p>
          <p className=' tw-underline'>
            By continuing to use the Services, You agree as follows:
          </p>
          <ol>
            <li className=' '>
              You are at least 18 years old or have been legally emancipated
            </li>
            <li className=' '>
              You understand and agree that these Terms are a legally binding
              agreement and the equivalent of a signed, written contract
            </li>
            <li className=' '>
              You will use the Services in a manner consistent with applicable
              laws and regulations and these Terms, as they may be amended by
              24HrDoc from time to time; and
            </li>
            <li className=' '>
              You understand, accept, and have received these Terms and the
              24HrDoc Privacy Policy, and acknowledge and demonstrate that You
              can access these Terms and the 24HrDoc Privacy Policy at will.
            </li>
          </ol>

          <p className=' '>
            <span className='tw-font-semibold'>
              If You do not agree with and accept the Terms, please discontinue
              all further use of the Services.
            </span>{' '}
            Do not log into the Website or Platform and immediately delete all
            files, if any, of the associated Website and Platform from Your
            device(s).
          </p>
          <p className=' tw-font-semibold'>
            <span className=' tw-underline '>ARBITRATION NOTICE:</span> EXCEPT
            IF YOU OPT-OUT AND EXCEPT FOR CERTAIN TYPES OF DISPUTES DESCRIBED IN
            THE dispute resolution section below, YOU AGREE THAT DISPUTES
            BETWEEN YOU AND 24HrDoc WILL BE RESOLVED BY BINDING, INDIVIDUAL
            ARBITRATION and you waive your right TO PARTICIPATE IN A className
            ACTION LAWSUIT OR className-WIDE ARBITRATION. YOU CAN OPT-OUT OF THE
            ARBITRATION AGREEMENT BY CONTACTING Services@24hrdoc.com WITHIN 30
            DAYS OF ACCEPTING THESE TERMS.
          </p>
          <p className=' tw-underline'>MAIN TERMS OF USE</p>
          <p className=' tw-font-semibold'>24HrDoc TERMS OF USE</p>
          <p className=' '>
            These Terms of Use ( &quot;Terms of Use &quot; or “Terms”) are a
            legal contract between you and 24HrDoc, Inc. (“
            <span className='tw-font-semibold'>us</span>”, “
            <span className='tw-font-semibold'>we</span>”, “
            <span className='tw-font-semibold'>our</span>, or{' '}
            <span className='tw-font-semibold'>24HrDoc</span>). 24HrDoc is the
            creator of the 24HrDoc website located at{' '}
            <a href='https://www.24hrdoc.com' target='_blank' rel='noreferrer'>
              www.24hrdoc.com
            </a>{' '}
            (the ”<span className='tw-font-semibold'>Website</span>”) and the
            24HrDoc patient platform (the “
            <span className='tw-font-semibold'>Platform</span>”). The 24HrDoc
            Website and Platform including all relevant content, products,
            services, and functionality associated with the Website and Platform
            and any other affiliated software or application owned by 24HrDoc,
            are collectively referred to as the “Services.” In this Agreement,
            the terms “you” and “yours” refer to the person using the Service.
            These Terms govern your use of the Services and by accepting these
            Terms and accessing or using the Services, You acknowledge that you
            have read, understand, and agree to these Terms and the 24HrDoc
            Privacy Policy. Any capitalized terms not defined in these Terms are
            defined in the 24HrDoc Privacy Policy.
          </p>
          <p className=' tw-font-semibold'>
            IF YOU ARE HAVING A MEDICAL EMERGENCY, OR have or suspect that you
            have an urgent medical problem or condition CALL 911 IMMEDIATELY.
            24hrdoc DOES NOT PROVIDE MEDICAL CARE.
          </p>
          <p className=' '>
            You may access and use the Services only in accordance with these
            Terms, and You agree to comply with all applicable laws, rules, and
            regulations, including any other policies incorporated into these
            Terms, such as the 24HrDoc Privacy Policy. With the exception of the
            Arbitration Agreement (see “Dispute Resolution” section) included
            near the end of these Terms, 24HrDoc reserves the right to change or
            modify these Terms at any time without prior notice to you. If we
            materially change or modify these Terms, we will let you know by (1)
            posting a new version of the Terms to the Website and Platform;
            and/or (2) posting a change notice on our Platform.
          </p>
          <p className=' '>
            If you continue to use the Services after we have informed you of
            the changes, you agree to be bound by the modified Terms. If you do
            not accept the changes, you should immediately stop using the
            Services and delete all files associated with the Services on your
            device(s).
          </p>
          <p className=' tw-underline tw-font-semibold'>SERVICES OVERVIEW</p>
          <p className=' '>
            The Website and Platform are designed to provide individuals who
            register as users of the Service (“Users”) with access a broad range
            of products and services. 24HrDoc works with independent affiliated
            professionals, including but not limited to licensed healthcare
            providers (“Providers”)(collectively, “Professionals”) to facilitate
            your access to clinical care, including lab tests and treatments,
            all via the 24HrDoc Platform. 24HrDoc provides non-clinical
            administrative and operational services to, and facilitates your
            connection and communication with, its affiliated Professionals via
            interactive real-time audio video technology as well as asynchronous
            communication methods, where deemed appropriate.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            Important information regarding HEALTHCARE services. PLEASE READ.
          </p>
          <p className='  tw-font-semibold'>
            24hrdoc is not a HEALTHCARE Provider. 24hrdoc IS a technology
            company that provides access to Providers, pharmacies, and labs, at
            the request of its customers. 24hrdoc DOES NOT PROVIDE MEDICAL
            ADVICE, CLINICAL CONSULTATIONS, DIAGNOSIS, OR TREATMENT. 24hrdoc has
            no ownership interest in affiliated Providers, does not have control
            over the clinical decision-making of the Providers, and is not
            responsible for the quality of care provided by the Providers. YOU
            SHOULD NOT DELAY TREATMENT OR IGNORE ADVICE FROM YOUR DOCTOR OR
            OTHER HEALTHCARE PROVIDER BASED ON INFORMATION PROVIDED BY 24hrdoc. 
            24hrdoc HEREBY DISCLAIMS ANY LIABILITY FOR ANY HEALTHCARE SERVICES
            PROVIDED BY A MEDICAL GROUP AND OBTAINED THROUGH THE SUPERPOWER
            SERVICES.
          </p>
          <p className=' '>
            By accepting these Terms, you agree and acknowledge that we do not
            provide clinical or medical services, laboratory or pharmacy
            services, or supplemental manufacturing services. Our role is
            limited to supporting and facilitating your access to these products
            and services via the Services.
          </p>
          <p className=' '>
            YOU AGREE AND ACKNOWLEDGE THAT 24hrdoc IS IN NO WAY PROVIDING
            DIAGNOSIS OR TREATMENT TO YOU. YOU FURTHER ACKNOWLEDGE AND AGREE
            THAT THE INFORMATION, PROCESSES, PRODUCTS, AND OTHER ITEMS
            REFERENCED AS PART OF THE SERVICES ARE NOT INTENDED AS A
            RECOMMENDATION OR ENDORSEMENT OF ANY COURSE OF ACTION, INFORMATION,
            OR PRODUCT. WE EXPLICITLY DISCLAIM THE CREATION OF A
            PROVIDER-PATIENT RELATIONSHIP WITH YOU.
          </p>
          <p className=' '>
            THE SERVICES CAN NOT AND ARE NOT DESIGNED, INTENDED, OR APPROPRIATE
            TO REPLACE THE RELATIONSHIP BETWEEN HEALTH CARE PROFESSIONALS AND
            PATIENTS OR TO ADDRESS SERIOUS, EMERGENT, OR LIFE-THREATENING
            MEDICAL CONDITIONS AND SHOULD NOT BE USED IN THOSE CIRCUMSTANCES.
          </p>
          <p className='  tw-font-semibold'>
            If at any time You are concerned about Your care or treatment, or
            You believe or suspect or someone else advises You that You have a
            serious or life-threatening condition, call 9-1-1 in areas where
            that service is available, or go to the nearest emergency room.
          </p>
          <p className=' '>
            Any medical advice provided by a Provider using information from the
            Services is based on the personal health data you provide. If you do
            not provide complete and accurate personal health information, the
            medical advice you receive may not be accurate or appropriate.
            Questions and information collected through the Services are
            designed for informational and/or research purposes and to identify
            potential patterns in symptomologies and treatments. The Services
            and/or any data derived from the Services are in no way intended to
            replace the independent clinical judgment of a qualified healthcare
            professional.
          </p>
          <p className=' '>
            FURTHER, A PROVIDER’S OR RESEARCHER’S USE OF OUR SERVICES IS NOT AN
            ENDORSEMENT OR RECOMMENDATION OF SUCH PROVIDER BY US.
          </p>
          <p className=' '>
            We do not confirm the credentials of any Provider using the
            Services. We do not validate that any such persons are in good
            standing with their respective licensure board(s) or that they are
            using the Services in accordance with laws applicable to the
            practice of medicine. It is YOUR responsibility to separately
            confirm that a healthcare provider is in good standing with his or
            her respective licensing board(s) and to exercise whatever other due
            diligence You feel is appropriate in selecting and maintaining your
            choice of healthcare professionals.
          </p>
          <p className='  tw-font-semibold'>
            General information available through the Services about medical
            conditions, symptomology, available drugs, treatment options, and
            other educational articles and videos is provided for general
            educational purposes only. Never disregard, avoid, or delay
            obtaining medical advice from a physician or other qualified
            healthcare professional because of something contained in the
            Services.
          </p>
          <p className=' tw-underline tw-font-semibold'>ACCESS TO SERVICES</p>
          <p className=' '>
            You must sign a membership agreement [or register] to create an
            account (“User Account”) and become a registered user to access any
            Services other than the Website. The registration process begins
            when you enter your demographic information (e.g., name, email
            address, contact information) into the Platform or Website. After
            entering the information, the user account will be created. To
            register, you must create a username and provide your name, your
            email address, and other information specified in the registration
            form (“<b>Registration Data</b>”). You may change or correct
            information in your account by visiting the user dashboard. You
            agree not to register for a User Account on behalf of an individual
            other than yourself unless you are legally authorized to bind such
            person to these Terms. By registering another person, you hereby
            represent that you are legally authorized to do so.
          </p>
          <p className=' '>
            By registering for an account and using the Services,{' '}
            <b>you represent and warrant as follows:</b>
          </p>
          <ol className=' tw-space-y-2'>
            {' '}
            <li className=' '>
              You are at least 18 years old and are otherwise legally qualified
              to enter into and form contracts under applicable law
            </li>
            <li className=' '>
              Your Registration Data is true, accurate, current, and complete
            </li>
            <li className=' '>
              You will update Your Registration Data as needed to maintain its
              accuracy
            </li>
            <li className=' '>
              You are authorized to create a User Account (either for yourself
              or another person)
            </li>
            <li className=' '>
              You acknowledge and agree to the terms of the 24HrDoc Privacy
              Policy
            </li>
            <li className=' '>
              You are legally authorized to view and share with Us health data
              and other Personal Information (as that term is defined in the
              24HrDoc Privacy Policy); and
            </li>
            <li className=' '>
              You are not located in a country that is subject to a U.S.
              Government embargo or that is designated by the U.S. Government as
              a “terrorist supporting” country, and You are not listed on any
              U.S. Government list of prohibited or restricted parties.
            </li>
          </ol>

          <p className=' tw-underline tw-font-semibold '>
            NOTE: THIS AGREEMENT IS VOID WHERE PROHIBITED BY LAW. DO NOT USE THE
            SERVICES WHERE PROHIBITED BY LAW. YOU UNDERSTAND THAT YOUR USE OF
            THE SERVICES MAY INVOLVE OR REQUIRE THE TRANSMISSION OF SIGNIFICANT
            AMOUNTS OF DATA. YOU ARE RESPONSIBLE FOR ALL DATA CHARGES THAT MAY
            BE CHARGED BY YOUR WIRELESS CARRIER OR INTERNET SERVICE PROVIDER OR
            THAT MAY OTHERWISE ARISE FROM YOUR USE OF THE SERVICES.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            Services Ownership and Use.
          </p>
          <p className=' '>
            24HrDoc owns the Services, including all content and functionality
            you access through the Services. Subject to your compliance with
            these Terms, 24HrDoc grants you a non-exclusive, non-sublicensable,
            revocable, non-transferable license to use the Services.
          </p>
          <p className=' tw-font-semibold'>
            THE SERVICES ARE FOR YOUR PERSONAL AND NON-COMMERCIAL USE ONLY AND
            YOU MAY NOT RESELL OR SUBLICENSE YOUR ACCESS TO THE SERVICES. You
            may not use the Services for any other purpose than what is allowed
            under these Terms without 24HrDoc&apos;s express written permission.
          </p>
          <p className=' '>
            You may not use 24HrDoc&apos;s name, trademarks, service marks, or
            logos, or those of third parties appearing on the Services in any
            advertising or publicity or to otherwise indicate 24HrDoc&apos;s or
            such third party&apos;s sponsorship or affiliation with any product
            or service without the express written permission from 24HrDoc or
            such third party.
          </p>
          <p className='tw-underline tw-font-semibold '>
            PERSONAl INFORMATION.
          </p>
          <p className=' '>
            You own your Personal Information and any other information you
            submit on or through the Services. If you are entering someone
            else&apos;s information into the Services, you represent and warrant
            that you have permission to do so. As a condition of using the
            Services, you grant to 24HrDoc a perpetual, non-exclusive, fully
            paid and royalty-free, transferable, sublicensable, worldwide
            license to use your Personal Information for the purpose of
            providing the Services, subject to applicable law and the
            restrictions in the 24HrDoc Privacy Policy. You agree to allow
            24HrDoc to de-identify and anonymize your Personal Information,
            including, without limitation, your Healthcare Information, in
            accordance with our Privacy Policy and applicable law, and to use or
            disclose such de-identified information for any purpose.
          </p>
          <p className=' tw-underline tw-font-semibold'>RESTRICTIONS ON USE.</p>
          <p className='tw-mb-4'>
            You may use the Services only for lawful purposes and in accordance
            with these Terms. In addition, we impose certain restrictions on
            your use of the Services. While using the Services, you shall not:
          </p>

          <ol className='tw-list-disc tw-space-y-2 www'>
            <li>
              Provide false, misleading, or inaccurate information to us or any
              other user
            </li>
            <li>
              Use the Services (i) for any commercial purpose; (ii) for the
              benefit of any third party; or (iii) in any manner not permitted
              by these Terms
            </li>
            <li>
              Impersonate or attempt to impersonate us, one of our employees,
              another user, or any other person or entity (including, without
              limitation, by using e-mail addresses or screen names associated
              with any of the foregoing)
            </li>
            <li>
              Use or attempt to use any manual process, engine, software, tool,
              agent, or other device or mechanism (including, without
              limitation, browsers, spiders, robots, avatars, or intelligent
              agents) to harvest, monitor, or otherwise collect information from
              the Services for any use, including, without limitation, use on
              third-party websites, without our consent
            </li>
            <li>
              Use the Services in any manner that could disable, overburden,
              damage, or impair the Services or interfere with any other
              party&apos;s use of the Services, including their ability to use
              the Services
            </li>
            <li>
              Access content or data not intended for you, or log onto a server
              or account that you are not authorized to access
            </li>
            <li>
              Violate any applicable federal, state, local, or international law
              or regulation (including, without limitation, any laws regarding
              the export of data or software to and from the U.S. or other
              countries)
            </li>
            <li>
              Attempt to probe, scan, or test the vulnerability of the Services
              or any associated system or network, or breach security or
              authentication measures without proper authorization
            </li>
            <li>
              Interfere or attempt to interfere with the use or functionality of
              the Services by any other user, host, or network, including,
              without limitation, by means of submitting a virus, trojan horse,
              worm, logic bomb, or other material, which is malicious or
              technologically harmful, overloading, &quot;flooding,&quot;
              &quot;spamming,&quot; &quot;mail bombing,&quot; or
              &quot;crashing&quot;
            </li>
            <li>
              Forge any TCP/IP packet header or any part of the header
              information in any e-mail or in any uploading or posting to, or
              transmission, display, performance, or distribution by means of,
              the Services
            </li>
            <li>
              Post or transmit any unsolicited advertising, promotional
              materials, &quot;junk mail,&quot; &quot;spam,&quot; &quot;chain
              letters,&quot; &quot;pyramid schemes,&quot; or any other form of
              solicitation
            </li>
            <li>
              Post, upload, publish, submit, or transmit any content that: (i)
              infringes, misappropriates, or violates a third party’s patent,
              copyright, trademark, trade secret, moral rights, or other
              intellectual property rights, or rights of publicity or privacy;
              (ii) violates, or encourages any conduct that would violate any
              applicable law or regulation or would give rise to civil
              liability; (iii) is fraudulent, false, misleading, or deceptive;
              (iv) is defamatory, obscene, pornographic, vulgar, or offensive;
              (v) promotes discrimination, bigotry, racism, hatred, harassment,
              or harm against any individual or group; (vi) is violent or
              threatening or promotes violence or actions that are threatening
              to any person or entity; or (vii) promotes illegal or harmful
              activities or substances
            </li>
            <li>
              Avoid, bypass, remove, deactivate, impair, descramble, or
              otherwise circumvent any technological measure implemented by us,
              you, or any other third-party (including another user) to protect
              the Services
            </li>
            <li>
              Attempt to modify, reverse-engineer, decompile, disassemble, or
              otherwise reduce or attempt to reduce to a human-perceivable form
              any of the source code used by us in providing the Services. Any
              violation of this section may subject you to civil and/or criminal
              liability
            </li>
            <li>
              Engage in any other conduct that restricts or inhibits
              anyone&apos;s use or enjoyment of the Services, or which, as
              determined by us, may harm us or other users of the Services or
              expose them to liability, or otherwise interfere with or attempt
              to interfere with the proper working of the Services
            </li>
            <li>
              Encourage or enable any other individual to do any of the above.
            </li>
          </ol>

          <p className='tw-mt-4'>
            24HrDoc is not obligated to monitor your use of the Services, but we
            may do so to ensure your compliance with these Terms, and/or to
            respond to law enforcement or other government agencies if and when
            we are required to. 24HrDoc reserves the right to suspend or
            terminate your use of the Services without notice to you if you
            partake in any of the prohibited uses described above.
          </p>

          <p className=' tw-underline tw-font-semibold'>
            [OPTIONAL: PROTECTING YOUR LOGIN INFORMATION.
          </p>
          <p className=' '>
            The Services are designed to require users to create a username and
            password to access and use the Services. Your username and password
            are, collectively, Your “User Credentials.” You are solely
            responsible for (i) maintaining the strict confidentiality of your
            User Credentials, (ii) not allowing another person to use your User
            Credentials to access the Services, and (iii) any and all damages or
            losses that may be incurred or suffered as a result of any
            activities that occur under your User Credentials, regardless of
            whether you were aware of those activities. You agree to immediately
            notify 24HrDoc in writing by email of any unauthorized use of your
            User Credentials or any other compromise of the security of your
            User Account.
          </p>
          <p className=' '>
            WE WILL NOT BE LIABLE FOR ANY LOSS THAT YOU INCUR AS A RESULT OF
            SOMEONE ELSE USING YOUR PASSWORD, EITHER WITH OR WITHOUT YOUR
            KNOWLEDGE. WE ARE NOT AND SHALL NOT BE LIABLE FOR ANY HARM ARISING
            FROM OR RELATING TO THE THEFT OF YOUR USER CREDENTIALS AND/OR ANY
            RESULTING ACCESS TO YOUR PERSONAL INFORMATION, YOUR DISCLOSURE OF
            YOUR USER CREDENTIALS, OR THE USE OF YOUR USER CREDENTIALS BY
            ANOTHER PERSON OR ENTITY REGARDLESS OF WHETHER YOU WERE AWARE OF
            SUCH USE.
          </p>
          <p className=' '>
            You may be held liable for any losses incurred by 24HrDoc and/or its
            affiliates, officers, directors, and representatives due to someone
            else&apos;s use of your User Account or password, regardless of
            whether you were aware of such use.]
          </p>
          <p className='tw-underline tw-font-semibold'>
            HOW WE PROTECT YOUR PRIVACY.
          </p>
          <p className=' '>
            24HrDoc values your privacy and is committed to keeping your
            Personal Information confidential. Please see the 24HrDoc Privacy
            Policy for an explanation of our privacy practices, the information
            we collect from you, how we use that information, and your rights
            regarding your Personal Information. By accessing or using the
            Services, or by downloading, viewing, or uploading any content
            through the Services, you acknowledge and agree to the provisions of
            the Privacy Policy and affirm that the Privacy Policy is a part of
            these Terms.
          </p>
          <p className=' '>
            By using the Services and accepting these Terms, you further
            acknowledge that 24HrDoc may share your Personal Information with
            third parties, including your Provider, accordance with the Privacy
            Policy. We will seek your consent to share your Personal Information
            with third parties before doing so where required by law.
          </p>
          <p className=' '>
            We are not responsible for nor liable to you or any third party for
            a third party&apos;s treatment of Personal Information, including
            any collection, use, disclosure, storage, loss, theft, or misuse of
            Personal Information, whether or not such treatment violates
            applicable law.
          </p>
          <p className=' '>
            Unless specifically stated in the 24HrDoc Privacy Policy, we do not
            control how third parties whom you share information collected,
            generated, or stored via the Services may further use and disclose
            such information. You expressly acknowledge and agree that 24HrDoc
            is neither responsible for nor liable to you or any third party for
            the treatment of your Personal Information by any such individual or
            entity, including any collection, use, disclosure, storage, loss,
            theft, or misuse of your Personal Information, whether or not such
            treatment violates applicable law.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            COMPUTER EQUIPMENT AND INTERNET ACCESS.
          </p>
          <p className=' '>
            You are responsible for obtaining, installing, maintaining, and
            operating all software, hardware, or other equipment (collectively,
            &quot;Systems &quot;) necessary for you to access and use the
            Services. This includes, without limitation, obtaining Internet
            services, using up-to-date web browsers, and the best commercially
            available encryption, antivirus, anti-spyware, and internet security
            software. You are responsible for the data security of the Systems
            used to access the Services and for the transmission and receipt of
            information using such Systems. We are not responsible for any
            errors or problems that arise from the malfunction or failure of the
            Internet or your Systems.
          </p>
          <p className=' '>
            THERE ARE ALWAYS CERTAIN SECURITY AND ACCESS AVAILABILITY RISKS
            ASSOCIATED WITH USING OPEN NETWORKS SUCH AS THE INTERNET, AND YOU
            EXPRESSLY ASSUME SUCH RISKS.
          </p>
          <p className=' '>ELECTRONIC COMMUNICATIONS.</p>
          <p className=' '>
            When you access or use the Services or send emails or text messages
            to us or to any Professional, you consent to receive communications
            from us and our affiliated Professionals electronically. We may
            communicate with you via email or via SMS text message.
          </p>
          <p className=' '>
            By choosing to use the Services, you are opting-in to send and
            receive text (SMS) messages to and from us and our affiliated
            Professionals regarding the Services. These communications may
            contain Personal Information and may also contain promotional and
            marketing material. If you consent to receive marketing or other
            communications not related to your use of the Services, we will
            provide you with the option to opt-out of such marketing
            communications within the applicable message.
          </p>
          <p className=' '>
            You represent and understand that: (1) you are the owner or
            authorized user of the mobile device you used in order to initiate
            the SMS Enrollment, (2) you are authorized to approve any applicable
            charges in connection with the text messages you send to and receive
            from us or our affiliated Professionals, and (3) you will be
            responsible for all messaging and other data charges that may apply
            for any text messages sent between you and 24hrdoc or our affiliated
            Professionals. WE DISCLAIM ANY LIABILITY FOR DELAYED OR UNDELIVERED
            MESSAGES.
          </p>
          <p className=' '>
            We may send communications, including emails, to you regarding your
            use of the Services. You can choose to filter any emails using your
            email settings, but we do not provide an option for you to opt-out
            of these communications. You can opt-out of any of our SMS services
            at any time by texting “STOP” to the message received, from the
            mobile device that is subscribed to receive the SMS messages. After
            you send the text message “STOP” to us, we may send you one final
            text message to confirm that you have been unsubscribed. If you have
            opted-in to receive more than one type of text message from us, you
            will need to opt-out of each SMS service to which you are
            subscribed. Until you have done so, you may continue to receive the
            types of text messages from us that you have opted-in to receive but
            have not unsubscribed from.
          </p>
          <p className=' '>
            You also understand that while 24HrDoc takes your privacy and the
            security of your health and other sensitive information very
            seriously, the transmission of information over the internet and
            mobile networks is not 100% secure. It is possible that electronic
            communications may be intercepted by third parties. If you choose to
            send or receive information about your health or any other sensitive
            information by text message or email, you do so at your own risk.
          </p>
          <p className=' '>
            You agree that all agreements, notices, disclosures and other
            communications that we provide to you electronically satisfy any
            legal requirement that such communications be in writing. You
            further agree that any notices provided by us electronically are
            deemed to be given and received on the date we transmit any such
            electronic communication as described in these Terms.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            Third-Party Websites.
          </p>
          <p className=' '>
            In the course of using the Services, you may be introduced to areas
            or features of the Services that allow you to access a website that
            does not belong to and are not controlled by us (collectively,
            “Third-Party Sites”). If You choose to access one of these
            Third-Party Sites, you will leave our Services and be redirected to
            an environment owned and controlled by an external third party. You
            acknowledge and agree that the Third-Party Sites may have different
            privacy policies, terms of use, user guides, and/or business
            practices (collectively, “Third-Party Rules”) than us, and that your
            use of such Third-Party Sites is governed exclusively by the
            respective Third-Party Rules. We provide links to Third-Party Sites
            to you as a convenience, and we do not verify, make any
            representations, or take responsibility for such Third-Party Sites,
            including, without limitation, the truthfulness, accuracy, quality,
            or completeness of the content, application, links displayed, and/or
            any other activities conducted on or through such Third-Party Sites.
          </p>
          <p className=' '>
            YOU AGREE THAT WE WILL NOT, UNDER ANY CIRCUMSTANCES, BE RESPONSIBLE
            OR LIABLE, DIRECTLY OR INDIRECTLY, FOR ANY GOODS, SERVICES, BUSINESS
            PRACTICES, INFORMATION, RESOURCES, APPLICATIONS, AND OTHER CONTENT
            (“Third Party Matters”) AVAILABLE ON OR THROUGH ANY THIRD-PARTY
            SITES OR THIRD-PARTY DEALINGS OR COMMUNICATIONS, OR FOR ANY HARM
            RELATED THERETO, OR FOR ANY DAMAGES OR LOSS CAUSED OR ALLEGED TO BE
            CAUSED BY OR IN CONNECTION WITH YOUR USE OR RELIANCE ON THE THIRD
            PARTY MATTERS.
          </p>
          <p className=' '>
            Any reference in the Services to any product, service, publication,
            institution, organization of any third-party entity, or individual
            does not constitute or imply our endorsement or recommendation.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            Third-Party Services.
          </p>
          <p className=' '>
            To the extent any features, aspects, products, or services offered
            through the Services are provided, in whole or in part, by third
            parties (“Third-Party Services” as provided by “Third-Party Service
            Providers”), You may be subject to additional terms and conditions.
            To the extent applicable, you will receive a notification and have
            the opportunity to accept such terms and conditions. IF YOU DO NOT
            UNDERSTAND OR DO NOT AGREE TO BE BOUND BY THOSE ADDITIONAL TERMS AND
            CONDITIONS, DO NOT USE THE RELATED THIRD-PARTY SERVICES.
          </p>
          <p className=' '>
            In the event of any inconsistency between terms of use relating to
            Third-Party Services and these Terms, those additional terms and
            conditions will control with respect to such Third-Party Services.
            Third-Party Service Providers may collect and use certain
            information about you, as specified in the Third-Party Service
            Provider&apos;s privacy policies. Prior to providing information to
            any Third-Party Service Provider, you should review their privacy
            policy. IF YOU DO NOT UNDERSTAND OR DO NOT AGREE TO THE TERMS OF A
            THIRD-PARTY SERVICE PROVIDER&apos;S PRIVACY POLICY OR TERMS OF USE,
            YOU SHOULD NOT USE THE RELATED THIRD-PARTY SERVICES. WE WILL NOT,
            UNDER ANY CIRCUMSTANCES, BE RESPONSIBLE OR LIABLE FOR ANY OF YOUR
            INFORMATION COLLECTED OR USED BY THIRD-PARTY SERVICE PROVIDERS.
          </p>
          <p className=' tw-underline tw-font-semibold'>TERMS OF SALE</p>
          <p className=' '>
            All products offered for sale by 24HrDoc are subject to availability
            and we reserve the right to impose quantity limits on any order or
            reject all or any part of an order without prior notice. In the
            event of an error, we reserve the right to correct the error and
            revise your order accordingly (which includes charging the correct
            price) or to cancel the order and refund any amount charged. Prices
            for products are subject to change at any time, but changes will not
            affect any order for products you have already placed. You are
            responsible for any applicable sales, use, duty, customs or other
            governmental taxes, levies or fees (“Taxes”) due with respect to
            your purchase of products or services through our Services. We will
            collect applicable Taxes if we determine we have a duty to collect
            Taxes. We will present an estimate of Taxes we collect at checkout,
            except where we have clearly stated in writing that a price includes
            Taxes. The actual Taxes charged may be adjusted from the amount
            shown at checkout. Several factors may cause this, such as variances
            between processor programs and changes in tax rates. We are not
            required to, and do not, collect Taxes in all states. You may have a
            duty to directly report and pay Taxes if we do not collect such
            Taxes.
          </p>
          <p className=' '>
            Only valid payment methods acceptable to us may be used to complete
            a purchase via the Services. You represent and warrant that you are
            authorized to use your designated payment method. You authorize us
            to charge your designated payment method for the total amount of
            your order (including any applicable taxes and shipping and handling
            charges). If any of the products in your order are unavailable, we
            will only charge the prices, Taxes, and other applicable charges
            associated with the products that are included in the shipment.
          </p>
          <p className=' '>
            You agree to pay any shipping and handling charges, if any, shown at
            the time you make a purchase. We reserve the right to increase,
            decrease, add, or eliminate shipping and handling charges from time
            to time, but we will provide notice of the changes applicable to you
            before you make your purchase. Any delivery dates or times shown as
            part of the checkout process are estimates only and are not
            guaranteed. Unless we state otherwise in writing via the Service,
            risk of loss or damage to a product passes to you upon delivery of
            the product to our designated carrier.
          </p>
          <p className=' '>
            In connection with any purchase you make through the Service, you
            may be asked to supply certain information relevant to the
            transaction, including, without limitation, your credit card number
            and expiration date, your billing address, your shipping address,
            your phone number and/or your email address. By submitting such
            information, you grant 24HrDoc without charge the irrevocable,
            unencumbered, universe-wide and perpetual right to provide such
            information to third parties (e.g., payment processing companies,
            buyers on the Service, sellers on the Service) for the purpose of
            facilitating the transaction.
          </p>
          <p className=' '>
            ​All credit card, debit card and other monetary transactions on or
            through the Services occur through an online payment processing
            application(s) accessible through the Services. This online payment
            processing application(s) is provided by 24HrDoc&apos;s third-party
            online payment processing vendor. Our processing vendor is a
            third-party, and is in no way subject to 24HrDoc&apos;s direction or
            control; thus, their relationship is not, and should not be
            construed as, one of fiduciaries, franchisors-franchisees,
            agents-principals, employers-employees, partners, joint venturers or
            the like.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            REPRESENTATIONS AND WARRANTIES.
          </p>
          <p className=' '>
            In addition to other representations and warranties contained
            throughout the Terms, you represent and warrant that your use of the
            Services will be in accordance with these Terms and all applicable
            laws, regulations, rules, and 24HrDoc policies and procedures (to
            the extent such policies and procedures are communicated to you).
            Specifically, you represent and warrant that you are legally
            authorized to share personal information (belonging to yourself or
            others on whose behalf you are submitting such personal information)
            with us.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            WARRANTY DISCLAIMERS.
          </p>
          <p className=' '>
            THE SERVICES ARE PROVIDED “AS IS,” WITHOUT WARRANTY OF ANY KIND,
            EITHER EXPRESS OR IMPLIED. WITHOUT LIMITING THE FOREGOING, 24hrdoc
            EXPLICITLY DISCLAIMS ANY WARRANTIES OF MERCHANTABILITY, FITNESS FOR
            A PARTICULAR PURPOSE, QUIET ENJOYMENT, NON-INFRINGEMENT, AND ANY
            WARRANTIES ARISING OUT OF THE COURSE OF DEALING OR USAGE OF TRADE.
            24HrDoc MAKES NO WARRANTY THAT THE SERVICES WILL MEET YOUR
            REQUIREMENTS OR BE AVAILABLE ON AN UNINTERRUPTED, SECURE, OR
            ERROR-FREE BASIS. 24hrdoc MAKES NO WARRANTY REGARDING THE QUALITY OF
            ANY PRODUCTS, APPLICATIONS, OR MATERIALS ACCESSED OR PURCHASED
            THROUGH THE APPLICATION OR THE ACCURACY, TIMELINESS, TRUTHFULNESS,
            COMPLETENESS, OR RELIABILITY OF ANY CONTENT OBTAINED THROUGH THE
            SERVICES.
          </p>
          <p className=' '>
            NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED FROM
            24hrdoc OR THROUGH THE APPLICATION OR MATERIALS, WILL CREATE ANY
            WARRANTY NOT EXPRESSLY MADE IN THESE TERMS.
          </p>
          <p className=' '>
            YOU ARE SOLELY RESPONSIBLE FOR ALL OF YOUR COMMUNICATIONS AND
            INTERACTIONS WITH THE SERVICES AND WITH OTHER PERSONS WITH WHOM YOU
            COMMUNICATE OR INTERACT AS A RESULT OF YOUR USE OF THE SERVICES,
            INCLUDING, WITHOUT LIMITATION, AFFILIATED PROFESSIONALS.
          </p>
          <p className=' '>
            YOU UNDERSTAND THAT 24hrdoc DOES NOT TAKE RESPONSIBILITY FOR
            [OPTIONAL: SCREENING OR INQUIRY INTO THE BACKGROUND OF ANY USERS OF
            THE SERVICES, INCLUDING, WITHOUT LIMITATION, PROVIDERS AND/OR
            CLINICIANS/CAREGIVERS.] 24hrdoc WILL NOT VERIFY OR VET THE
            STATEMENTS OF ANY PROFESSIONALS. 24hrdoc MAKES NO REPRESENTATIONS OR
            WARRANTIES AS TO THE CONDUCT OF AFFILIATED PROFESSIONALS.
          </p>
          <p className=' '>
            24hrdoc CAN NOT ALWAYS FORESEE OR ANTICIPATE TECHNICAL OR OTHER
            DIFFICULTIES THAT MAY RESULT IN FAILURE TO OBTAIN DATA OR LOSS OF
            DATA, PERSONALIZATION SETTINGS, OR OTHER SERVICE INTERRUPTIONS.
            24hrdocCAN NOT ASSUME RESPONSIBILITY FOR THE TIMELINESS, ACCURACY,
            DELETION, NON-DELIVERY, OR FAILURE TO STORE ANY USER DATA,
            COMMUNICATIONS, OR PERSONALIZATION SETTINGS. IT IS YOUR
            RESPONSIBILITY TO BACKUP ANY INFORMATION YOU ENTER INTO THE WEBSITE
            OR PLATFORM.
          </p>
          <p className=' '>
            YOU AGREE THAT YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK. YOU
            WILL NOT HOLD 24HrDoc OR ITS AFFILIATED PROFESSIONALS RESPONSIBLE
            FOR ANY LOSS OR DAMAGE THAT RESULTS FROM YOUR ACCESS TO OR USE OF
            THE SERVICES, INCLUDING, WITHOUT LIMITATION, ANY LOSS OR DAMAGE TO
            ANY OF YOUR COMPUTERS, MOBILE DEVICES, OR DATA.
          </p>
          <p className=' '>
            YOU ACKNOWLEDGE AND AGREE THAT, TO THE MAXIMUM EXTENT PERMITTED BY
            LAW, THE ENTIRE RISK ARISING OUT OF YOUR ACCESS TO AND USE OF THE
            SERVICES REMAINS WITH YOU. NEITHER 24HrDoc NOR ANY OTHER PERSON OR
            ENTITY INVOLVED IN CREATING, PRODUCING, OR DELIVERING THE SERVICES
            WILL BE LIABLE FOR ANY INCIDENTAL, SPECIAL, EXEMPLARY, OR
            CONSEQUENTIAL DAMAGES, INCLUDING LOST PROFITS, LOSS OF DATA, LOSS OF
            GOODWILL, SERVICE INTERRUPTION, COMPUTER DAMAGE OR SYSTEM FAILURE,
            OR THE COST OF SUBSTITUTE PRODUCTS OR APPLICATIONS, OR FOR ANY
            DAMAGES FOR PERSONAL OR BODILY INJURY OR EMOTIONAL DISTRESS ARISING
            OUT OF OR IN CONNECTION WITH THESE TERMS OR FROM THE USE OF OR
            INABILITY TO USE THE SERVICES, OR FROM ANY COMMUNICATIONS,
            INTERACTIONS, OR MEETINGS WITH OTHER USERS OF THE SERVICES OR OTHER
            PERSONS WITH WHOM YOU COMMUNICATE OR INTERACT AS A RESULT OF YOUR
            USE OF THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT, TORT
            (INCLUDING NEGLIGENCE), PRODUCT LIABILITY, OR ANY OTHER LEGAL
            THEORY, AND WHETHER OR NOT 24HrDoc HAS BEEN INFORMED OF THE
            POSSIBILITY OF SUCH DAMAGE, EVEN IF A LIMITED REMEDY SET FORTH IN
            THESE TERMS IS FOUND TO HAVE FAILED IN MEETING ITS ESSENTIAL
            PURPOSE.
          </p>
          <p className=' '>
            IF YOU ARE DISSATISFIED WITH THE SERVICES OR THESE TERMS, YOUR ONLY
            REMEDY IS TO DISCONTINUE USING THE SERVICES.
          </p>
          <p className=' '>
            24hrdoc IS NOT LIABLE TO ANY USER OR PERSON FOR ANY HARM CAUSED BY
            THE NEGLIGENCE OR MISCONDUCT OF ANY AFFILIATED PROFESSIONALS,
            WHETHER OR NOT RELYING UPON INFORMATION COLLECTED, GENERATED, OR
            STORED VIA THE SERVICES. IF YOU ARE DISSATISFIED WITH THE SERVICES
            YOU ARE RECEIVING FROM AN AFFILIATED PROVIDER, YOU MAY HAVE
            ADDITIONAL REMEDIES REGARDING THE PROVISION OF HEALTHCARE SERVICES.
          </p>
          <p className=' '>
            YOU ACKNOWLEDGE THAT IF YOU USE THE SERVICES DURING OR IN RELATION
            TO AN EMERGENT, SERIOUS, OR LIFE-THREATENING CONDITION, SUCH USE IS
            AT YOUR SOLE RISK. 24HrDoc IS NOT LIABLE TO YOU OR ANY PERSON FOR
            ANY DECISION MADE OR ACTION TAKEN IN RELIANCE UPON INFORMATION
            INCLUDED AS PART OF THE SERVICES.
          </p>
          <p className=' '>
            BECAUSE SOME STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF
            LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES, SOME OF THE ABOVE
            LIMITATIONS MAY NOT APPLY TO YOU. IN SUCH STATES, 24hrdoc’S
            LIABILITY IS LIMITED AND WARRANTIES ARE EXCLUDED TO THE GREATEST
            EXTENT PERMITTED BY LAW, BUT SHALL, IN NO EVENT, EXCEED $100.00. ANY
            CLAIM ARISING FROM THE USAGE OF THE SERVICES MUST BE BROUGHT WITHIN
            TWO (2) YEARS OF THE OCCURRENCE OF THE EVENT FROM WHICH THE CLAIM
            AROSE.
          </p>
          <p className=' tw-underline tw-font-semibold'>INDEMNIFICATION.</p>
          <p className=' '>
            You agree to indemnify, defend, and hold harmless 24HrDoc and its
            representatives from any third party liability, loss, claim, suit,
            damage, and expense (including reasonable attorneys&apos; fees and
            expenses) arising out of or in any way connected with your access to
            or use of the services, your violation of these terms, or any
            negligent or wrongful conduct by you or related to your user account
            by you or any other person accessing the services through your user
            account, regardless of whether you were aware of such use.
          </p>
          <p className=' '>
            IF YOU ARE A CALIFORNIA RESIDENT, YOU WAIVE CALIFORNIA CIVIL CODE
            SECTION 1542, WHICH STATES: “A GENERAL RELEASE DOES NOT EXTEND TO
            CLAIMS THAT THE CREDITOR OR RELEASING PARTY DOES NOT KNOW OR SUSPECT
            TO EXIST IN HIS OR HER FAVOR AT THE TIME OF EXECUTING THE RELEASE
            AND THAT, IF KNOWN BY HIM OR HER, WOULD HAVE MATERIALLY AFFECTED HIS
            OR HER SETTLEMENT WITH THE DEBTOR OR RELEASED PARTY.”
          </p>
          <p className=' tw-underline tw-font-semibold'>
            PROVIDING FEEDBACK TO US.
          </p>
          <p className=' '>
            We welcome and encourage you to provide feedback, comments, and
            suggestions for improvements to the Services (“Feedback”). You may
            submit Feedback by e-mailing Us at services@24hrdoc.com. You
            acknowledge and agree that if you submit any Feedback to Us, you
            grant to us a non-exclusive, worldwide, perpetual, irrevocable,
            fully-paid, royalty-free, sub-licensable, and transferable license
            under any and all intellectual property rights that you own or
            control to use, copy, modify, create derivative works based upon,
            and otherwise exploit the Feedback for any purpose.
          </p>
          <p className=' tw-underline tw-font-semibold'>ACCOUNT TERMINATION.</p>
          <p className=' '>
            If you breach any of these Terms, we may suspend or disable your
            account or terminate your access to the Services without prior
            notice to you. There may be other instances where we may need to
            terminate your access to the Services that are not related to any of
            your actions or inactions. We reserve the right to terminate your
            access to and use of the Services and materials at any time, with or
            without cause.
          </p>
          <p className=' '>
            If you wish to terminate your account, please contact 24HrDoc at
            [INSERT EMAIL], immediately discontinue your use of the Services,
            and delete all files associated with the Services from your computer
            or mobile device.
          </p>
          <p className=' tw-underline tw-font-semibold'>
            Digital Millennium Copyright Act Policy
          </p>
          <p className=' '>
            The Digital Millennium Copyright Act of 1998 (“DMCA”) provides
            recourse for copyright owners who believe that material appearing on
            the Internet infringes their rights under U.S. copyright law. If you
            believe in good faith that materials appearing on this Website
            infringe your copyright, you (or your agent) may send us a notice
            requesting that the material be removed, or access to it blocked. In
            addition, if you believe in good faith that a notice of copyright
            infringement has been wrongly filed against you, the DMCA permits
            you to send us a counter-notice. Notices and counter-notices must
            meet statutory requirements imposed by the DMCA. One place to find
            more information is the U.S. Copyright Office Web site, currently
            located at . Any written Notification of Claimed infringement should
            comply with Title 17, United States Code, Section 512(c)(3)(A) and
            should be provided in writing to [INSERT EMAIL].
          </p>
          <p className='tw-underline tw-font-semibold'>
            Class Action Waiver &amp; Mandatory Arbitration Agreement
          </p>
          <p className='tw-font-semibold'>
            Dispute Resolution by Binding Arbitration
          </p>
          <p className='tw-font-semibold'>
            PLEASE READ THIS SECTION CAREFULLY BECAUSE IT REQUIRES YOU TO
            ARBITRATE CERTAIN DISPUTES AND CLAIMS WITH 24HRDOC AND LIMITS THE
            MANNER IN WHICH YOU MAY SEEK RELIEF FROM US.
          </p>
          <p className=' '>
            By using the services provided by 24hrdoc.com, you agree that any
            dispute, claim, or controversy arising out of or relating to your
            use of the website, telehealth services, communications with
            providers, prescriptions, billing, privacy practices, or these Terms
            and Conditions shall be resolved exclusively through final and
            binding arbitration, rather than in court, except as otherwise
            required by applicable law.
          </p>
          <p className=' '>
            This arbitration agreement shall be governed by the Federal
            Arbitration Act (&quot;FAA&quot;) and applicable federal law.
          </p>
          <p className='tw-underline tw-font-semibold'>Waiver of Jury Trial</p>
          <p className='tw-font-semibold'>
            YOU AND 24HRDOC EACH WAIVE THE RIGHT TO A TRIAL BY JURY IN ANY
            ACTION OR PROCEEDING ARISING OUT OF OR RELATED TO THESE TERMS OR
            THE SERVICES PROVIDED THROUGH THE PLATFORM.
          </p>
          <p className='tw-underline tw-font-semibold'>Class Action Waiver</p>
          <p className='tw-font-semibold'>
            YOU AGREE THAT ANY CLAIMS AGAINST 24HRDOC SHALL BE BROUGHT ONLY IN
            YOUR INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN
            ANY PURPORTED CLASS ACTION, COLLECTIVE ACTION, MASS ACTION, PRIVATE
            ATTORNEY GENERAL ACTION, OR REPRESENTATIVE PROCEEDING.
          </p>
          <p className=' '>
            Unless both you and 24HRDOC agree otherwise in writing:
          </p>
          <ul className='tw-list-disc tw-space-y-2 tw-pl-6'>
            <li>
              The arbitrator may not consolidate more than one person&apos;s
              claims;
            </li>
            <li>
              The arbitrator may not preside over any form of class or
              representative proceeding; and
            </li>
            <li>
              The arbitrator may award relief only in favor of the individual
              party seeking relief and only to the extent necessary to resolve
              that individual party&apos;s claim.
            </li>
          </ul>
          <p className='tw-underline tw-font-semibold'>
            Arbitration Procedures
          </p>
          <p className=' '>
            Any arbitration shall be administered by the American Arbitration
            Association (&quot;AAA&quot;) under its Consumer Arbitration Rules,
            or another mutually agreed arbitration provider.
          </p>
          <p className=' '>
            The arbitration may be conducted remotely, by telephone, video
            conference, written submissions, or in the county where the user
            resides, unless otherwise required by law.
          </p>
          <p className=' '>
            Each party shall bear its own attorneys&apos; fees and costs unless
            otherwise awarded by the arbitrator under applicable law.
          </p>
          <p className='tw-underline tw-font-semibold'>Exceptions</p>
          <p className=' '>
            Nothing in this agreement shall prevent either party from:
          </p>
          <ul className='tw-list-disc tw-space-y-2 tw-pl-6'>
            <li>
              Seeking relief in small claims court for qualifying disputes;
            </li>
            <li>
              Seeking injunctive or equitable relief related to intellectual
              property or unauthorized access to the platform;
            </li>
            <li>
              Reporting concerns to governmental or regulatory authorities.
            </li>
          </ul>
          <p className='tw-underline tw-font-semibold'>Opt-Out Right</p>
          <p className=' '>
            Users may opt out of this arbitration agreement by sending written
            notice within thirty (30) days of first accepting these Terms. The
            opt-out request must include the user&apos;s full name, email
            address associated with the account, and a clear statement that the
            user wishes to opt out of arbitration.
          </p>
          <p className=' '>Opt-out notices should be sent to:</p>
          <p className='tw-font-semibold'>24hrdoc Legal Department</p>
          <p className=' '>3050 Post Oak Blvd</p>
          <p className=' '>Houston, TX 77056</p>
          <p className=' '>
            Email:{' '}
            <a
              href='mailto:services@24hrdoc.com'
              className='tw-text-blue-600 hover:tw-underline'
            >
              services@24hrdoc.com
            </a>
          </p>
          <p className='tw-underline tw-font-semibold'>Severability</p>
          <p className=' '>
            If any portion of this Arbitration Agreement or Class Action Waiver
            is found unenforceable, the remaining portions shall remain in full
            force and effect, except that if the Class Action Waiver is found
            unenforceable, then this entire Arbitration Agreement shall be null
            and void.
          </p>
          <p className='tw-underline tw-font-semibold'>Acknowledgment</p>
          <p className='tw-font-semibold'>
            BY USING THE SERVICES OF 24HRDOC, YOU ACKNOWLEDGE THAT YOU HAVE
            READ, UNDERSTOOD, AND AGREE TO THIS BINDING ARBITRATION AGREEMENT
            AND CLASS ACTION WAIVER, AND THAT YOU VOLUNTARILY WAIVE CERTAIN
            RIGHTS TO PARTICIPATE IN COURT PROCEEDINGS OR CLASS ACTIONS.
          </p>
          <p className=' tw-underline tw-font-semibold'>MISCELLANEOUS TERMS.</p>
          <p className=' tw-font-semibold'>Entire Agreement.</p>
          <p className=' '>
            These Terms, the 24HrDoc Privacy Policy, and any other terms
            incorporated by reference, constitute the entire and exclusive
            understanding and agreement between 24HrDoc and You regarding the
            Services, and these Terms supersede and replace any and all prior
            oral or written understandings or agreements between 24HrDoc and You
            regarding the Services.
          </p>
          <p className=' tw-font-semibold'>Governing Law.</p>
          <p className=' '>
            These Terms shall be governed by the laws of the State of Texas
            without reference to its conflict of laws provisions.
          </p>
          <p className=' tw-font-semibold'>Assignment.</p>
          <p className=' '>
            You may not assign or transfer these Terms, by operation of law or
            otherwise, without 24HrDoc’s prior written consent. Any attempt by
            you to assign or transfer these Terms, without such consent, will be
            null and of no effect. 24HrDoc may assign or transfer these Terms,
            at its sole discretion, without restriction. Subject to the
            foregoing, these Terms will bind and inure to the benefit of the
            parties, their successors, and permitted assigns.
          </p>
          <p className=' tw-font-semibold'>Notices.</p>
          <p className=' '>
            Any notices or other communications permitted or required under
            these Terms, including those regarding modifications to these Terms,
            will be in writing and given: (i) by 24HrDoc via email (in each case
            to the address that You provide); and/or (ii) by posting to the
            Website [or Platform]. For notices made by email, the notice will be
            effective as of the date the notice is first transmitted. You agree
            that any notice received from 24HrDoc electronically satisfies any
            legal requirement that such notice be in writing. YOU ALONE ARE
            RESPONSIBLE FOR ENSURING THAT YOUR EMAIL ADDRESS ON FILE WITH
            24HrDoc IS ACCURATE AND CURRENT, AND NOTICE TO YOU SHALL BE
            EFFECTIVE UPON THE SENDING OF AN EMAIL TO THE ADDRESS WE HAVE ON
            FILE.
          </p>
          <p className=' '>
            You shall give any notice to 24HrDoc by email to:
            services@24hrdoc.com. Notice to 24HrDoc shall be effective upon
            receipt of notice by 24HrDoc.
          </p>
          <p className=' tw-font-semibold'>No Inadvertent Waiver.</p>
          <p className=' '>
            The failure of 24HrDoc to enforce any right or provision of these
            Terms will not constitute a waiver of future enforcement of that
            right or provision. The waiver of any such right or provision will
            be effective only if in writing and signed by a duly authorized
            representative of 24HrDoc.
          </p>
          <h4 className='tw-text-lg tw-font-semibold tw-text-gray-900'>
            <strong>Severability.</strong>
          </h4>
          <p className=' '>
            If any provision of these Terms is determined to be invalid,
            illegal, or unenforceable, the remaining provisions of these Terms
            remain in full force, provided that the essential terms and
            conditions remain valid, binding, and enforceable and the economic
            and legal substance of the transactions contemplated by these Terms
            are materially preserved.
          </p>
          <p className=' tw-font-semibold'>Intellectual Property Rights.</p>
          <p className=' '>
            “Intellectual Property Rights” means all intellectual property
            rights or similar proprietary rights, including (i) patent rights
            and utility models, (ii) copyrights and database rights, (iii)
            trademarks trade names, domain names, and trade dress and the
            goodwill associated therewith, (iv) trade secrets, (v) mask works,
            and (vi) industrial design rights; in each case, including any
            registrations of, applications to register, and renewals and
            extensions of, any of the foregoing in any jurisdiction in the
            world.
          </p>
          <p className=' '>
            As between you and us, all right, title, and interest, including all
            Intellectual Property Rights, in the Services, any related
            materials, logos, products, and documentation, and any other
            property or materials furnished or made available hereunder, and all
            modifications and enhancements thereof, belong to and are retained
            solely by 24HrDoc or its licensors, vendors, and affiliates, as
            applicable. All rights not expressly granted are reserved by us. Any
            use of the Services not expressly permitted by these Terms is a
            breach of these Terms and may violate copyright, trademark, and
            other laws.
          </p>
          <p className=' tw-font-semibold'>Remedies.</p>
          <p className=' '>
            Except as expressly set forth in these Terms, the exercise by either
            party of any of its remedies under these Terms will be without
            prejudice to its other remedies under these Terms or otherwise. If,
            for any reason, a court of competent jurisdiction finds any
            provision of these Terms invalid or unenforceable, that provision
            will be enforced to the maximum extent permissible, and the other
            provisions of these Terms will remain in full force and effect.
          </p>
          <p className='tw-font-semibold '>Contacting 24hrdoc</p>
          <p className=' '>
            Please feel free to contact Us if you have any questions about these
            Terms and/or any other documents referenced in these Terms. You may
            contact us at services@24hrdoc.com or at our mailing address:
          </p>
          <p className=' tw-font-semibold'>24HrDoc, Inc.</p>
          <p className=' '>3050 Post Oak Blvd, Houston, TX 77056</p>
          <p className=' '>
            Data Security Officer: Muhammad Sabir (muhammad.sabir@24hrdoc.com)
          </p>
        </div>

        {/* Footer contact block */}
        <footer className='tw-mt-12 tw-pt-6 tw-border-t tw-border-gray-100'>
          <h3 className='tw-text-2xl tw-font-semibold tw-text-gray-900 '>
            Contact 24HrDoc
          </h3>
          <p className='  tw-mt-1'>
            Have questions about these Terms? Email us at{' '}
            <a
              className='tw-text-blue-600 hover:tw-underline'
              href='mailto:services@24hrdoc.com'
            >
              services@24hrdoc.com
            </a>
            .
          </p>
        </footer>
      </Container>
    </div>
  );
}

export default TermsConditions;
